import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import type { InfoLinksConfig } from "@/types/agent/info-links";
import type { GenieAttachment } from "@/types/voice/genie-attachment";

const DISPLAY_LIMIT = 5;

export interface FilterableMessage {
  message: string;
  isUser: boolean;
  attachment?: GenieAttachment;
}

interface FilteredResourcesOptions {
  /**
   * The genie shows links and pictures in the chat itself (Show Links & Images).
   * Those cards are then the "relevant right now" surface and the rail is not
   * shown, so the first-message pass is skipped. The sheet and wrap-up passes
   * still run, with the resources the genie already showed listed first.
   */
  inChatMedia?: boolean;
}

const shownFirst = <T>(items: T[], isShown: (item: T) => boolean): T[] => [
  ...items.filter(isShown),
  ...items.filter((item) => !isShown(item)),
];

/**
 * Filters a genie's info-links resources down to the ones relevant to a
 * conversation, using the AI to judge relevance from the conversation transcript.
 *
 * The AI call only ever fires at three points, never continuously:
 *  1. as soon as the visitor's first message exists (there's nothing for the
 *     AI to reason about before that — a bare "connected" state or the
 *     agent's own welcome message says nothing about what the visitor wants)
 *  2. when the visitor opens the Resources sheet (`sheetOpen` becomes true) —
 *     re-filters with whatever transcript exists by then
 *  3. when the conversation ends (`conversationEnded` becomes true) — a final
 *     pass so a post-call summary reflects the whole conversation
 *
 * Skipped entirely when the resource count already fits the display limit —
 * nothing to filter out. Until the first filter pass resolves, this returns
 * `null` rather than the raw list — callers (the rail) should treat `null`
 * as "not ready yet" and stay hidden rather than show an unfiltered guess.
 *
 * With `inChatMedia` the first trigger is dropped: see FilteredResourcesOptions.
 *
 * Voice conversations persist their transcript via TranscriptStorageService;
 * text-only conversations don't, so `liveMessages` (the in-memory chat
 * history) is used as a fallback when nothing is stored yet.
 */
export const useFilteredResources = (
  conversationId: string | null | undefined,
  resources: InfoLinksConfig | null,
  conversationActive: boolean,
  sheetOpen: boolean,
  conversationEnded: boolean,
  liveMessages?: FilterableMessage[],
  { inChatMedia = false }: FilteredResourcesOptions = {},
): InfoLinksConfig | null => {
  const links = resources?.links ?? [];
  const documents = resources?.documents ?? [];
  const totalCount = links.length + documents.length;
  const withinLimit = totalCount <= DISPLAY_LIMIT;

  const shownUrls = useMemo(
    () =>
      new Set(
        (liveMessages ?? [])
          .filter((m) => m.attachment?.source === "resource")
          .map((m) => m.attachment!.url),
      ),
    [liveMessages],
  );

  // Trigger 1: the visitor's first message. Voice conversations persist to
  // TranscriptStorageService; text-only ones only have liveMessages, so both
  // are checked.
  const storedHasUserMessage =
    !!conversationId && !!TranscriptStorageService.getStoredTranscript(conversationId)?.messages?.some((m) => m.isUser);
  const liveHasUserMessage = !!liveMessages?.some((m) => m.isUser);
  const firstPassArmed = conversationActive && (storedHasUserMessage || liveHasUserMessage);

  // Trigger 3: conversation end — a one-shot latch per conversationId so it
  // fires once even though `conversationEnded` may stay true afterwards.
  const endFiredForRef = useRef<string | null>(null);
  const [endPassArmed, setEndPassArmed] = useState(false);
  useEffect(() => {
    if (!conversationEnded || !conversationId || withinLimit) return;
    if (endFiredForRef.current === conversationId) return;
    endFiredForRef.current = conversationId;
    setEndPassArmed(true);
  }, [conversationEnded, conversationId, withinLimit]);

  // Each trigger is its own pass over a growing transcript, so it must be a
  // distinct query — reusing one queryKey across triggers would let React
  // Query serve the 5s-in answer forever instead of re-running with more
  // conversation to go on when the sheet opens or the call ends.
  const pass = endPassArmed ? "end" : sheetOpen ? "sheet" : firstPassArmed && !inChatMedia ? "first" : "idle";
  const shouldFilter = pass !== "idle";

  const { data } = useQuery({
    queryKey: ["filtered-resources", conversationId, pass, links.map((l) => l.id), documents.map((d) => d.id)],
    queryFn: async () => {
      const stored = conversationId ? TranscriptStorageService.getStoredTranscript(conversationId) : null;
      const transcript = stored
        ? TranscriptStorageService.convertToElevenLabsFormat(stored)
        : (liveMessages ?? [])
            .filter((m) => m.message)
            .map((m) => ({
            role: (m.isUser ? "user" : "agent") as "user" | "agent",
            message: m.message,
            time_in_call_secs: 0,
          }));

      const { data, error } = await supabase.functions.invoke("handle-conversation", {
        body: {
          action: "filter-resources",
          data: {
            conversationId,
            transcript,
            links: links.map((link) => ({ id: link.id, label: link.label, description: link.description ?? null })),
            documents: documents.map((doc) => ({
              id: doc.id,
              label: doc.label || doc.fileName,
              description: doc.description ?? null,
            })),
          },
        },
      });

      if (error) throw new Error(error.message || "Failed to filter resources");
      return data as { linkIds: string[]; documentIds: string[] };
    },
    enabled: shouldFilter && !!conversationId && !withinLimit,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  if (withinLimit) return resources;
  if (!resources) return null;
  if (!inChatMedia) {
    if (!data) return null;
    const linkIdSet = new Set(data.linkIds);
    const documentIdSet = new Set(data.documentIds);
    return {
      ...resources,
      links: links.filter((link) => linkIdSet.has(link.id)),
      documents: documents.filter((doc) => documentIdSet.has(doc.id)),
    };
  }

  const linkIdSet = new Set(data?.linkIds);
  const documentIdSet = new Set(data?.documentIds);
  const linkShown = (link: InfoLinksConfig["links"][number]) => shownUrls.has(link.url);
  const documentShown = (doc: NonNullable<InfoLinksConfig["documents"]>[number]) => shownUrls.has(doc.fileUrl);

  return {
    ...resources,
    links: shownFirst(
      links.filter((link) => linkShown(link) || linkIdSet.has(link.id)),
      linkShown,
    ),
    documents: shownFirst(
      documents.filter((doc) => documentShown(doc) || documentIdSet.has(doc.id)),
      documentShown,
    ),
  };
};
